import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import AdminPaymentEventsPageView from './AdminPaymentEventsPageView';
import { useSupportedProviders } from '../admin/useSupportedProviders'; // task-36: canonical provider list from backend
import PaymentEventDetailModal from './PaymentEventDetailModal';
import {
  bulkRetryPaymentEvents,
  fetchAllUnprocessedIds,
  fetchPaymentEvents,
  fetchPaymentEvent,
  retryPaymentEvent,
  type BulkRetrySummary,
  type PaymentEventDetail,
  type PaymentEventItem,
  type PaymentEventsFilters,
  type PaymentEventsPagination
} from './adminPaymentEvents.api';

interface State {
  loading: boolean;
  error: string | null;
  events: PaymentEventItem[];
  pagination: PaymentEventsPagination;
  draftFilters: PaymentEventsFilters; // user-editable filters before Apply
  appliedFilters: PaymentEventsFilters; // filters last sent to the API
  // task-16: detail modal state. Kept here (not in the view) so closing the
  // modal doesn't lose the loaded detail mid-fade and so a single source of
  // truth governs open/loading/error.
  detailOpen: boolean;
  detailLoading: boolean;
  detailError: string | null;
  detail: PaymentEventDetail | null;
}

type Action =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_RESULTS'; payload: { events: PaymentEventItem[]; pagination: PaymentEventsPagination } }
  | { type: 'SET_DRAFT'; payload: PaymentEventsFilters }
  | { type: 'APPLY_FILTERS'; payload: PaymentEventsFilters }
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'REPLACE_EVENT'; payload: PaymentEventItem } // task-15: in-place row update after a retry, avoids a full refetch
  | { type: 'REPLACE_EVENTS'; payload: PaymentEventItem[] } // task-18: batch in-place row update after a bulk retry
  | { type: 'RESET' }
  | { type: 'DETAIL_OPEN' }
  | { type: 'DETAIL_LOADING'; payload: boolean }
  | { type: 'DETAIL_ERROR'; payload: string | null }
  | { type: 'DETAIL_LOADED'; payload: PaymentEventDetail }
  | { type: 'DETAIL_CLOSE' };

// task-15: lightweight transient banner shown after a retry attempt so staff
// can see the outcome without inspecting the row twice. Cleared on next action.
interface RetryFeedback {
  level: 'success' | 'error';
  message: string;
}

// task-18: live progress + summary state for bulk retries. `current`/`total`
// drive the inline "Retried X of Y…" indicator; `summary` (set on completion)
// powers the post-batch banner that breaks down successes/failures/skips.
interface BulkProgress {
  current: number;
  total: number;
}

const DEFAULT_FILTERS: PaymentEventsFilters = {
  provider: '',
  kind: '',
  processed: '',
  startDate: '',
  endDate: '',
  page: 1,
  limit: 25
};

// task-18: chunk size for bulk retry calls. Smaller than the server's
// MAX_BULK_SIZE (100) so the UI updates progress more frequently and so a
// single chunk's worst-case duration stays under typical proxy timeouts.
const BULK_CHUNK_SIZE = 10;

// task-18: hard cap on rows the "Select all matching filter" walk will
// gather. Prevents accidental million-row selections; staff can still
// re-apply the action after each batch if recovery exceeds this bound.
const SELECT_ALL_MAX = 1000;

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_LOADING': return { ...state, loading: action.payload };
    case 'SET_ERROR': return { ...state, error: action.payload, loading: false };
    case 'SET_RESULTS': return { ...state, events: action.payload.events, pagination: action.payload.pagination, error: null, loading: false };
    case 'SET_DRAFT': return { ...state, draftFilters: action.payload };
    case 'APPLY_FILTERS': return { ...state, appliedFilters: { ...action.payload, page: 1 }, draftFilters: { ...action.payload, page: 1 } };
    case 'SET_PAGE': return { ...state, appliedFilters: { ...state.appliedFilters, page: action.payload } };
    case 'REPLACE_EVENT': {
      // task-15: match by `id` (Mongo `_id` online, or `provider:eventId`
      // composite offline) rather than `eventId`, since `eventId` is NOT
      // unique across providers. Falling back to (provider + eventId) covers
      // the case where the payload arrived without an `id`.
      const next = state.events.map(ev => {
        if (action.payload.id && ev.id === action.payload.id) return action.payload;
        if (!action.payload.id && ev.provider === action.payload.provider && ev.eventId === action.payload.eventId) return action.payload;
        return ev;
      });
      return { ...state, events: next };
    }
    case 'REPLACE_EVENTS': {
      // task-18: index incoming updates by `id` so the merge is O(rows + updates)
      // instead of O(rows * updates). Mirrors REPLACE_EVENT's matching rules.
      const byId = new Map<string, PaymentEventItem>();
      const byEventKey = new Map<string, PaymentEventItem>();
      for (const upd of action.payload) {
        if (upd.id) byId.set(upd.id, upd);
        if (upd.eventId) byEventKey.set(`${upd.provider}|${upd.eventId}`, upd);
      }
      const next = state.events.map(ev => {
        if (ev.id && byId.has(ev.id)) return byId.get(ev.id)!;
        const k = `${ev.provider}|${ev.eventId}`;
        if (byEventKey.has(k)) return byEventKey.get(k)!;
        return ev;
      });
      return { ...state, events: next };
    }
    case 'RESET': return { ...state, draftFilters: { ...DEFAULT_FILTERS }, appliedFilters: { ...DEFAULT_FILTERS } };
    case 'DETAIL_OPEN': return { ...state, detailOpen: true, detailLoading: true, detailError: null, detail: null };
    case 'DETAIL_LOADING': return { ...state, detailLoading: action.payload };
    case 'DETAIL_ERROR': return { ...state, detailError: action.payload, detailLoading: false };
    case 'DETAIL_LOADED': return { ...state, detail: action.payload, detailLoading: false, detailError: null };
    case 'DETAIL_CLOSE': return { ...state, detailOpen: false, detailLoading: false, detailError: null, detail: null };
    default: return state;
  }
}

function rowIdOf(ev: PaymentEventItem): string {
  // task-18: same identity rule the table uses — prefer the listing-emitted
  // `id` (Mongo `_id` online, or `provider:eventId` composite offline) and
  // fall back to the bare eventId only when the API didn't surface an `id`.
  return ev.id || ev.eventId || '';
}

function AdminPaymentEventsPage() {
  const [state, dispatch] = useReducer(reducer, {
    loading: true,
    error: null,
    events: [],
    pagination: { page: 1, limit: 25, total: 0, totalPages: 0 },
    draftFilters: { ...DEFAULT_FILTERS },
    appliedFilters: { ...DEFAULT_FILTERS },
    detailOpen: false,
    detailLoading: false,
    detailError: null,
    detail: null
  });

  // task-22: mirror the resolver pattern from SocketContext so this module can
  // also be required under Jest/CommonJS (where bare `import.meta` is a syntax
  // error). At runtime in Vite this returns the populated env object exactly
  // as before.
  const importMetaEnv = (() => {
    try { return new Function('return import.meta.env')(); } catch { return (globalThis as any).__import_meta_env || {}; }
  })();
  const envKey = importMetaEnv?.VITE_QreditCharge_API_KEY as string | undefined;
  const lsKey = typeof window !== 'undefined' ? window.localStorage?.getItem('QreditCharge_API_KEY') || undefined : undefined;
  const apiKeyMissing = !envKey && !lsKey;
  const { providers: supportedProviders } = useSupportedProviders(); // task-36: fetch canonical list from backend

  const load = useCallback(async (filters: PaymentEventsFilters) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const result = await fetchPaymentEvents(filters);
      dispatch({ type: 'SET_RESULTS', payload: result });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to load payment events';
      dispatch({ type: 'SET_ERROR', payload: message });
    }
  }, []);

  useEffect(() => { load(state.appliedFilters); }, [state.appliedFilters, load]);

  // task-16: load full detail when an admin clicks a row. Open immediately so
  // the loading state is visible and the user gets feedback even on slow links.
  const openDetail = useCallback(async (id: string) => {
    if (!id) return; // explicit no-op rather than firing a request that would 400
    dispatch({ type: 'DETAIL_OPEN' });
    try {
      const detail = await fetchPaymentEvent(id);
      dispatch({ type: 'DETAIL_LOADED', payload: detail });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to load payment event';
      dispatch({ type: 'DETAIL_ERROR', payload: message });
    }
  }, []);

  const closeDetail = useCallback(() => dispatch({ type: 'DETAIL_CLOSE' }), []);

  // task-15: per-event retry state — `retryingId` blocks double-clicks while
  // a request is in-flight; `retryFeedback` surfaces the outcome inline.
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [retryFeedback, setRetryFeedback] = useState<RetryFeedback | null>(null);

  // task-18: bulk selection + retry state. Selection lives in a Set keyed by
  // row id so toggling is O(1). `bulkRunning` blocks the action buttons while
  // a batch is in flight, `bulkProgress` powers the inline progress text, and
  // `bulkSummary` powers the post-batch breakdown banner.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<BulkProgress | null>(null);
  const [bulkSummary, setBulkSummary] = useState<BulkRetrySummary | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [selectAllInFlight, setSelectAllInFlight] = useState(false);

  // Drop selections that the latest fetch has proven to be already-processed
  // (those would just 409 on the server). Selections for ids NOT visible on
  // the current page are preserved so staff can accumulate selections across
  // pages — only ids the server has now confirmed as processed are pruned.
  useEffect(() => {
    setSelectedIds(prev => {
      if (prev.size === 0) return prev;
      const visibleProcessed = new Set(
        state.events.filter(ev => ev.processed).map(rowIdOf).filter(Boolean)
      );
      if (visibleProcessed.size === 0) return prev; // nothing new to prune
      let changed = false;
      const next = new Set<string>();
      prev.forEach(id => {
        if (visibleProcessed.has(id)) { changed = true; return; }
        next.add(id);
      });
      return changed ? next : prev;
    });
  }, [state.events]);

  const onRetry = useCallback(async (event: PaymentEventItem) => {
    // task-15: prefer the unique row `id` (Mongo `_id` online, or the
    // `provider:eventId` composite offline) over the bare `eventId`, since
    // `eventId` can collide across providers (same `${timestamp}_${kind}`
    // composite is legitimately reusable). Falling back to `eventId` only
    // covers historical rows where the API didn't surface an `id`.
    const targetId = event.id || event.eventId;
    if (!targetId) {
      setRetryFeedback({ level: 'error', message: 'Event is missing an identifier; cannot retry.' });
      return;
    }
    setRetryingId(targetId);
    setRetryFeedback(null);
    try {
      const result = await retryPaymentEvent(targetId);
      dispatch({ type: 'REPLACE_EVENT', payload: result.event });
      if (result.success) {
        setRetryFeedback({ level: 'success', message: `Retried ${result.event.kind || 'event'} successfully.` });
      } else {
        setRetryFeedback({ level: 'error', message: result.message || 'Retry failed.' });
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Retry request failed';
      setRetryFeedback({ level: 'error', message });
    } finally {
      setRetryingId(null);
    }
  }, []);

  // task-18: pre-compute the unprocessed row ids on the current page so the
  // "Select all on page" header checkbox and the "selected count" badge can
  // both reference the same source of truth.
  const visibleUnprocessedIds = useMemo(
    () => state.events.filter(ev => !ev.processed).map(rowIdOf).filter(Boolean),
    [state.events]
  );

  const toggleSelected = useCallback((id: string) => {
    if (!id) return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAllOnPage = useCallback(() => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      visibleUnprocessedIds.forEach(id => next.add(id));
      return next;
    });
  }, [visibleUnprocessedIds]);

  const clearPageSelection = useCallback(() => {
    setSelectedIds(prev => {
      if (prev.size === 0) return prev;
      const next = new Set(prev);
      visibleUnprocessedIds.forEach(id => next.delete(id));
      return next;
    });
  }, [visibleUnprocessedIds]);

  const clearAllSelection = useCallback(() => setSelectedIds(new Set()), []);

  // task-18: walk every page of the listing endpoint to collect ids of all
  // unprocessed events that match the currently-applied filters. Surfaces
  // any truncation to staff so they know the selection was bounded.
  const selectAllMatching = useCallback(async () => {
    setSelectAllInFlight(true);
    setBulkError(null);
    try {
      const { ids, truncated, total } = await fetchAllUnprocessedIds(state.appliedFilters, { maxIds: SELECT_ALL_MAX });
      if (ids.length === 0) {
        setBulkError(`No unprocessed events match the current filter${total > 0 ? ` (out of ${total} total).` : '.'}`);
        return;
      }
      setSelectedIds(new Set(ids));
      if (truncated) {
        setBulkError(`Selected the first ${ids.length} unprocessed events; ${total - ids.length} more match the filter and will need a follow-up retry.`);
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to enumerate matching events';
      setBulkError(message);
    } finally {
      setSelectAllInFlight(false);
    }
  }, [state.appliedFilters]);

  // task-18: drive the batch retry. Splits ids into BULK_CHUNK_SIZE-sized
  // requests so progress updates between chunks; per-chunk results are
  // merged into the table in place (REPLACE_EVENTS) and aggregated into a
  // running summary that survives the loop. Stops accepting new chunks
  // after the first transport error so we don't keep hammering a broken
  // backend, but still surfaces the partial summary for what completed.
  const retrySelected = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkRunning(true);
    setBulkSummary(null);
    setBulkError(null);
    setBulkProgress({ current: 0, total: ids.length });

    const aggregate: BulkRetrySummary = { total: ids.length, succeeded: 0, failed: 0, skipped: 0, notFound: 0 };
    const completedIds = new Set<string>();
    let aborted = false;

    for (let offset = 0; offset < ids.length; offset += BULK_CHUNK_SIZE) {
      const chunk = ids.slice(offset, offset + BULK_CHUNK_SIZE);
      try {
        const response = await bulkRetryPaymentEvents(chunk);
        aggregate.succeeded += response.summary.succeeded;
        aggregate.failed += response.summary.failed;
        aggregate.skipped += response.summary.skipped;
        aggregate.notFound += response.summary.notFound;
        const updates = response.results
          .map(r => r.event)
          .filter((ev): ev is PaymentEventItem => !!ev);
        if (updates.length > 0) {
          dispatch({ type: 'REPLACE_EVENTS', payload: updates });
        }
        response.results.forEach(r => completedIds.add(r.id));
      } catch (e: unknown) {
        // Treat the whole chunk as failed for accounting purposes so the
        // summary numbers still add up to `total`. Surface the message and
        // stop processing further chunks — repeated failures usually mean
        // the backend is unhealthy and continuing would just produce noise.
        aggregate.failed += chunk.length;
        const message = e instanceof Error ? e.message : 'Bulk retry request failed';
        setBulkError(message);
        aborted = true;
      } finally {
        setBulkProgress({ current: Math.min(offset + chunk.length, ids.length), total: ids.length });
      }
      if (aborted) break;
    }

    setBulkSummary(aggregate);
    setBulkRunning(false);
    // Clear selections that completed (succeeded, failed, or skipped) so the
    // user can re-select the still-pending rows for another attempt without
    // having to manually deselect what already ran.
    setSelectedIds(prev => {
      if (completedIds.size === 0) return prev;
      const next = new Set<string>();
      prev.forEach(id => { if (!completedIds.has(id)) next.add(id); });
      return next;
    });
  }, [selectedIds]);

  return (
    <>
      <AdminPaymentEventsPageView
        loading={state.loading}
        error={state.error}
        events={state.events}
        pagination={state.pagination}
        filters={state.draftFilters}
        onFilterChange={(next) => dispatch({ type: 'SET_DRAFT', payload: next })}
        onApply={() => dispatch({ type: 'APPLY_FILTERS', payload: state.draftFilters })}
        onReset={() => dispatch({ type: 'RESET' })}
        onPageChange={(page) => dispatch({ type: 'SET_PAGE', payload: page })}
        onRowSelect={openDetail}
        apiKeyMissing={apiKeyMissing}
        onRetry={onRetry}
        retryingId={retryingId}
        retryFeedback={retryFeedback}
        selectedIds={selectedIds}
        onToggleSelected={toggleSelected}
        onSelectAllOnPage={selectAllOnPage}
        onClearPageSelection={clearPageSelection}
        onClearAllSelection={clearAllSelection}
        onSelectAllMatching={selectAllMatching}
        onRetrySelected={retrySelected}
        bulkRunning={bulkRunning}
        bulkProgress={bulkProgress}
        bulkSummary={bulkSummary}
        bulkError={bulkError}
        selectAllInFlight={selectAllInFlight}
        visibleUnprocessedCount={visibleUnprocessedIds.length}
        supportedProviders={supportedProviders} // task-36: backend-sourced list replaces hard-coded options
      />
      <PaymentEventDetailModal
        open={state.detailOpen}
        loading={state.detailLoading}
        error={state.detailError}
        detail={state.detail}
        onClose={closeDetail}
      />
    </>
  );
}

export default AdminPaymentEventsPage;
